import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react'

import { client, settingInterceptor } from 'shared/services/ServiceConfig'

const chave = '@TMovFrotista'
const trocaGestorPath = 'reset-register'

interface ISignInCredentials {
  access_token: string
  expires_in: number
  refresh_expires_in: number
  refresh_token: string
  token_type: string
  session_state: string
  scope: string
  expiry_date_token: Date
  expiry_date_refresh_token: Date
  user: string
  name: string
  usuario_id: number
  perfil: string
  telefone: string
}

export interface IAuthContextState {
  user: string
  signIn(credentials: ISignInCredentials): void
  signOut(): void
  name: string
  usuario_id: number
  perfil: string
  telefone: string
}

interface IAuthState {
  token: string
  refresh_token: string
  user: string
  name: string
  usuario_id: number
  perfil: string
  telefone: string
}

export const recoverUserFromStorage = (key: string): IAuthState => {
  const token = localStorage.getItem(`${key}:token`)
  const refresh_token = localStorage.getItem(`${key}:refreshToken`)
  const user = localStorage.getItem(`${key}:user`)
  const name = localStorage.getItem(`${key}:name`)
  const telefone = localStorage.getItem(`${key}:telefone`)
  const perfil = localStorage.getItem(`${key}:perfil`)
  const usuario_id = Number(localStorage.getItem(`${key}:usuarioId`))
  return {
    token,
    refresh_token,
    user,
    name,
    telefone,
    perfil,
    usuario_id,
  }
}

const AuthContext = createContext<IAuthContextState>({} as IAuthContextState)

export function useAuth(): IAuthContextState {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}

export const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<IAuthState>()
  const pathName = window.location.pathname
  const clearData = () => {
    localStorage.clear()
    client.defaults.headers.authorization = ''

    setData({} as IAuthState)
  }
  const signOut = useCallback(async () => {
    const accessToken = localStorage.getItem(`${chave}:token`)
    const refreshToken = localStorage.getItem(`${chave}:refreshToken`)

    try {
      await client.post(
        '/auth/logout',
        {
          refresh_token: data?.refresh_token || refreshToken,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      clearData()
    } catch (error) {
      clearData()
    }
  }, [data?.refresh_token])
  const signIn = useCallback(
    async (signinCredential: ISignInCredentials) => {
      const {
        user,
        access_token,
        refresh_token,
        name,
        usuario_id,
        perfil,
        telefone,
      } = signinCredential

      settingInterceptor(access_token, chave, signIn, signOut, pathName)
      localStorage.setItem(`${chave}:token`, access_token)
      localStorage.setItem(`${chave}:user`, user)
      localStorage.setItem(`${chave}:refreshToken`, refresh_token)
      localStorage.setItem(`${chave}:name`, name)
      localStorage.setItem(`${chave}:perfil`, perfil)
      localStorage.setItem(`${chave}:usuarioId`, usuario_id.toString())
      localStorage.setItem(`${chave}:telefone`, telefone)
      localStorage.setItem(`${chave}:aviso`, 'true')

      setData({
        user,
        token: access_token,
        refresh_token,
        name,
        usuario_id,
        perfil,
        telefone,
      })
    },
    [signOut],
  )

  useEffect(() => {
    const reload = async () => {
      const token = localStorage.getItem(`${chave}:token`)
      settingInterceptor(token, chave, signIn, signOut, pathName)
    }
    reload()
    const { token, refresh_token, user, name, telefone, perfil, usuario_id } =
      recoverUserFromStorage(chave)
    settingInterceptor(token, chave, signIn, signOut, pathName)

    // Não deve fazer o login se for a rota para trocar o gestor
    if (token && user && refresh_token && !pathName.includes(trocaGestorPath)) {
      setData({
        token,
        user,
        refresh_token,
        name,
        usuario_id,
        perfil,
        telefone,
      })
    }
  }, [signIn, signOut])

  return (
    <AuthContext.Provider
      value={{
        user: data?.user,
        signIn,
        signOut,
        name: data?.name,
        usuario_id: data?.usuario_id,
        perfil: data?.perfil,
        telefone: data?.telefone,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
